import { AdFormat, CUSTOM_AD_FORMAT, INTERSTITIAL, Mediatype, MOBILE_INTERSTITIAL, OUT_OF_PAGE } from './adFormat'
import { DeviceType } from './device-type'
import { ScopeId } from './scope'
import { byName } from './sort'

export type AnchorFeature = {
  /** to handle forms an undefined mode means it's disabled  */
  mode: 'useExistingDiv' | 'createNewDiv' | undefined
}

export type AdUnitFeature = {
  dynamicEnabled: boolean
  headerBiddingEnabled: boolean
  parallaxEnabled?: boolean
  skinEnabled?: boolean
  anchor?: AnchorFeature
  canOutstream: boolean
  isInstream: boolean
  nativeTemplate?: string
}

export type ContainerMaxSize = {
  width?: number
  height?: number
}

export type KeyValuesTargeting = {
  key: string
  values: string[]
}

export type AdmAdUnitInstreamParams = {
  /**
   * @deprecated Legacy 0-indexed playback option. Use `playbackmethod` (ORTB2, 1-indexed) instead.
   * Still written (kept in sync with `playbackmethod - 1`) so already-deployed stacks / older
   * kleanads.js builds — which only read this field — keep rendering correctly during the
   * transition. Remove once every client reads `playbackmethod`.
   */
  startPlayingOption?: number
  /** ORTB2 `playbackmethod` (1-indexed, 1..6). Canonical replacement for `startPlayingOption`. */
  playbackmethod?: number
  skippableads: boolean
  protocols: string[]
  mediaTypeParams: {
    pos: number
    minduration: number
    maxduration: number
    minbitrate: number
    maxbitrate: number
    placement: number
    plcmt: number
  }
}

/**
 * Resolve the ORTB2 `playbackmethod` (1-indexed) from instream params, falling back to the
 * legacy 0-indexed `startPlayingOption` (`+ 1`) for data that predates the `playbackmethod` field.
 */
export const resolveInstreamPlaybackMethod = (params: AdmAdUnitInstreamParams): number | undefined =>
  params.playbackmethod ?? (params.startPlayingOption != null ? params.startPlayingOption + 1 : undefined)

/**
 * Normalize instream params on write: set the canonical `playbackmethod` and keep the deprecated
 * `startPlayingOption` in sync (`playbackmethod - 1`) so legacy readers stay correct. Idempotent.
 */
export const syncInstreamPlaybackMethod = (params: AdmAdUnitInstreamParams): AdmAdUnitInstreamParams => {
  const playbackmethod = resolveInstreamPlaybackMethod(params)
  // Neither field set: leave both keys absent rather than spreading explicit `undefined`, which the
  // Mongo driver would persist as a literal `null` (it does not ignore undefined by default).
  if (playbackmethod == null) return { ...params }
  return {
    ...params,
    playbackmethod,
    startPlayingOption: playbackmethod - 1,
  }
}

export type AdUnit = {
  id: string
  scopeId: ScopeId
  enabled: boolean
  name: string
  adServerAdUnitName: string
  divId: string
  sizes: string[]
  customSizes: string[]
  fluid: boolean
  slotKeyValues?: Array<KeyValuesTargeting>
  devices: DeviceType[]
  adFormat?: AdFormat
  containerMaxSize?: ContainerMaxSize
  instreamParams?: AdmAdUnitInstreamParams
} & AdUnitFeature

export type DisplayedAdUnit = Pick<
  AdUnit,
  | 'id'
  | 'name'
  | 'adServerAdUnitName'
  | 'divId'
  | 'enabled'
  | 'headerBiddingEnabled'
  | 'canOutstream'
  | 'isInstream'
  | 'fluid'
  | 'sizes'
  | 'customSizes'
  | 'dynamicEnabled'
  | 'slotKeyValues'
  | 'devices'
  | 'adFormat'
  | 'containerMaxSize'
  | 'skinEnabled'
  | 'parallaxEnabled'
  | 'anchor'
  | 'nativeTemplate'
  | 'instreamParams'
>

export const VALIDATE_ADUNIT_SIZE_REGEX = /^\d+[x]\d+$/

/**
 * Columns of the mapping export uploaded to S3 by the mapping export lambda.
 * These strings are an external artifact contract: do not rename them.
 */
export const ADUNIT_PROPERTIES = {
  'Pbstck Ad Unit Name': 'adUnitName',
  'Pbstck Ad Unit Code In The Ad Server': 'adServerAdUnitName',
  'Pbstck Technical Identification (div ID)': 'divId',
  'Pbstck Device': 'device',
  'Pbstck Standard Sizes': 'sizes',
} as const

export const AD_UNIT_CSV_HEADERS: (keyof typeof ADUNIT_PROPERTIES)[] = [
  'Pbstck Ad Unit Name',
  'Pbstck Ad Unit Code In The Ad Server',
  'Pbstck Technical Identification (div ID)',
  'Pbstck Device',
  'Pbstck Standard Sizes',
]

/** Number of `Key-value N` columns of the ad units export. */
export const AD_UNIT_EXPORT_KEY_VALUE_COLUMN_COUNT = 5

/**
 * Columns of the ad units export downloaded from the ad units page.
 * The import accepts this format too, so both must stay in sync.
 */
export const AD_UNIT_EXPORT_HEADERS: string[] = [
  'Name',
  'Archived',
  'Ad unit name in the ad server',
  'Technical identification (div ID)',
  'Devices',
  'Format',
  'Dynamic',
  'Parallax',
  'Max size width',
  'Max size height',
  'Header bidding',
  'Banner',
  'Fluid size',
  'Standard sizes',
  'Custom sizes',
  'Outstream',
  'Instream',
  // Holds the template id rather than its name: the id is what the ad unit stores, so the round trip needs no
  // lookup and cannot land on the wrong template when two of them share a name.
  'Native template',
  ...[...Array(AD_UNIT_EXPORT_KEY_VALUE_COLUMN_COUNT).keys()].map((i) => `Key-value ${i + 1}`),
]

/**
 * The shape of the export before it carried the native template. Frozen on purpose rather than derived from the
 * list above: it describes files already downloaded and kept on disk, so it must not follow a future column.
 * The import still accepts it, reading `Native template` as empty.
 */
export const AD_UNIT_EXPORT_HEADERS_WITHOUT_NATIVE_TEMPLATE: string[] = [
  'Name',
  'Archived',
  'Ad unit name in the ad server',
  'Technical identification (div ID)',
  'Devices',
  'Format',
  'Dynamic',
  'Parallax',
  'Max size width',
  'Max size height',
  'Header bidding',
  'Banner',
  'Fluid size',
  'Standard sizes',
  'Custom sizes',
  'Outstream',
  'Instream',
  ...[...Array(AD_UNIT_EXPORT_KEY_VALUE_COLUMN_COUNT).keys()].map((i) => `Key-value ${i + 1}`),
]

/**
 * Columns of the ad units export that the import reads, the other ones are informative only.
 * Everything below `sizes` tells the import where an ad unit takes its inventory from when it declares no
 * standard size — without them a native, outstream, fluid or out-of-page ad unit could not be read back.
 */
export const AD_UNIT_EXPORT_PROPERTIES = {
  Name: 'adUnitName',
  'Ad unit name in the ad server': 'adServerAdUnitName',
  'Technical identification (div ID)': 'divId',
  Devices: 'device',
  'Standard sizes': 'sizes',
  Format: 'format',
  'Custom sizes': 'customSizes',
  'Fluid size': 'fluid',
  Outstream: 'outstream',
  Instream: 'instream',
  'Native template': 'nativeTemplate',
} as const

/** Devices and sizes are `,` separated in the mapping export and ` | ` separated in the ad units export. */
export const AD_UNIT_CSV_MULTI_VALUE_SEPARATOR = ' | '
export const SPLIT_ADUNIT_CSV_MULTI_VALUE_REGEX = /\s*\|\s*|\s*,\s*/

/** Quote whatever would otherwise break the row apart, doubling the inner quotes as the CSV format wants. */
const csvCell = (value: string): string => (/[",\n]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value)

/**
 * Builds the ad units export downloaded from the ad units page. Lives next to the headers it fills so that a
 * column added on one side cannot silently desynchronise from the other: every row is padded to the header arity,
 * which is what the import needs to read the file back.
 */
export const buildAdUnitsExportCSV = (adUnits: AdUnit[]): string => {
  const convertBoolean = (bool?: boolean): string => (bool ? 'enabled' : 'disabled')
  const convertArray = (array: string[]): string => array.join(AD_UNIT_CSV_MULTI_VALUE_SEPARATOR)

  const displayAdUnit = (adUnit: AdUnit): string => {
    const mediatypes = getAdUnitMediatypes(adUnit)
    const keyValues = adUnit.slotKeyValues?.map(({ key, values }) => `${key} = ${convertArray(values)}`) ?? []
    return [
      adUnit.name,
      adUnit.enabled ? 'no' : 'yes',
      adUnit.adServerAdUnitName,
      adUnit.divId,
      // `devices` is required on the type but absent on some stored ad units — the same reason
      // `isDeviceEnabled` below guards it. Spreading `undefined` throws and takes the whole export down.
      convertArray([...(adUnit.devices ?? [])].sort()),
      adUnit.adFormat?.name ?? CUSTOM_AD_FORMAT.name,
      convertBoolean(adUnit.dynamicEnabled),
      convertBoolean(adUnit.parallaxEnabled),
      adUnit.containerMaxSize?.width ? `${adUnit.containerMaxSize.width}px` : '',
      adUnit.containerMaxSize?.height ? `${adUnit.containerMaxSize.height}px` : '',
      convertBoolean(adUnit.headerBiddingEnabled),
      convertBoolean(mediatypes.includes('Banner')),
      convertBoolean(adUnit.fluid),
      convertArray(adUnit.sizes),
      convertArray(adUnit.customSizes),
      convertBoolean(mediatypes.includes('Outstream')),
      convertBoolean(mediatypes.includes('Instream')),
      adUnit.nativeTemplate ?? '',
      // One column per key-value, padded so that every row holds as many fields as the header.
      ...[...Array(AD_UNIT_EXPORT_KEY_VALUE_COLUMN_COUNT).keys()].map((i) => keyValues[i] ?? ''),
    ]
      .map(csvCell)
      .join(',')
  }

  const enabledAdUnits = adUnits.filter((adUnit) => adUnit.enabled).sort(byName)
  const archivedAdUnits = adUnits.filter((adUnit) => !adUnit.enabled).sort(byName)

  return [AD_UNIT_EXPORT_HEADERS.join(','), ...[...enabledAdUnits, ...archivedAdUnits].map(displayAdUnit)].join('\n')
}

export type AdUnitImportValidationResult = { line: number; name: string; errors?: string[] }
export type AdUnitsImportValidationResults = { errors?: string[]; adUnits?: AdUnitImportValidationResult[] }

export const isDeviceEnabled = (device: DeviceType, devices?: string[]): boolean => {
  return (devices || []).some((d) => d === device)
}

export const getAdUnitMediatypes = (adunit: AdUnit | DisplayedAdUnit): Mediatype[] => {
  const mediatypes: Mediatype[] = []
  if (!adunit.isInstream && (adunit.fluid || adunit.sizes.length > 0 || adunit.customSizes.length > 0)) {
    mediatypes.push('Banner')
  }
  if (adunit.canOutstream) {
    mediatypes.push('Outstream')
  }
  if (adunit.nativeTemplate) {
    mediatypes.push('Native')
  }
  if (adunit.isInstream) {
    mediatypes.push('Instream')
  }
  return mediatypes
}

export const isOutOfPage = (adFormatId: string): boolean => adFormatId === OUT_OF_PAGE.id || adFormatId === MOBILE_INTERSTITIAL.id || adFormatId === INTERSTITIAL.id
